import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCurrentUser } from "../auth/hooks";
import { BUDDIES_QUERY_KEY } from "../buddies/types";
import { client } from "../client";
import { USER_CLUBS_QUERY_KEY } from "../clubs/types";
import { MONTHLY_LEADERBOARD_QUERY_KEY } from "../leaderboards/types";
import {
  MILESTONES_QUERY_KEY,
  MILESTONES_RECAP_QUERY_KEY,
} from "../milestones/types";
import { MISSIONS_QUERY_KEY } from "../missions/types";
import { USER_PROFILE_QUERY_KEY } from "../users/types";
import {
  ACTIVITIES_QUERY_KEY,
  Activity,
  ClaimActivityRequest,
  ClaimActivityResponse,
  ReactToActivityRequest,
  ReactToActivityResponse,
  RECENT_CLUB_ACTIVITIES_QUERY_KEY,
  UploadActivityRequest,
  UploadActivityResponse,
} from "./types";

/**
 * Hook for uploading a new activity
 * @returns Mutation for uploading an activity
 */
export function useUploadActivity() {
  const queryClient = useQueryClient();

  return useMutation<
    UploadActivityResponse,
    { message: string; status: number },
    UploadActivityRequest
  >({
    mutationFn: async (data) => {
      const response = await client.activities.$post({
        json: data,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw {
          message: errorData.message || "Failed to upload activity",
          status: response.status,
        };
      }

      return await response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch activities
      queryClient.invalidateQueries({ queryKey: ACTIVITIES_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: RECENT_CLUB_ACTIVITIES_QUERY_KEY,
      });
      // Also invalidate leaderboard queries
      queryClient.invalidateQueries({
        queryKey: MONTHLY_LEADERBOARD_QUERY_KEY,
      });
      // Also invalidate milestones queries
      queryClient.invalidateQueries({
        queryKey: MILESTONES_RECAP_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: MILESTONES_QUERY_KEY,
      });
    },
  });
}

/**
 * Hook for deleting an activity
 * @returns Mutation for deleting an activity
 */
export function useDeleteActivity() {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: async (activityId) => {
      const response = await client.activities[":id"].$delete({
        param: { id: activityId },
      });

      if (!response.ok) {
        const errorData = (await response.json()) as { message?: string };
        throw new Error(errorData.message || "Failed to delete activity");
      }

      const result = await response.json();
      return result;
    },
    onSuccess: () => {
      // Invalidate and refetch activities
      queryClient.invalidateQueries({ queryKey: ACTIVITIES_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: RECENT_CLUB_ACTIVITIES_QUERY_KEY,
      });
      // Also invalidate leaderboard queries
      queryClient.invalidateQueries({
        queryKey: MONTHLY_LEADERBOARD_QUERY_KEY,
      });
      // Also invalidate milestones queries
      queryClient.invalidateQueries({
        queryKey: MILESTONES_RECAP_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: MILESTONES_QUERY_KEY,
      });
    },
  });
}

/**
 * Hook for claiming an activity
 * @returns Mutation for claiming an activity with boost
 */
export function useClaimActivity() {
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();

  return useMutation<
    ClaimActivityResponse,
    { message: string; status: number },
    ClaimActivityRequest
  >({
    mutationFn: async (data) => {
      const response = await client.activities.claim.$post({
        json: data,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw {
          message: errorData.message || "Failed to claim activity",
          status: response.status,
        };
      }

      return await response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch activities
      queryClient.invalidateQueries({ queryKey: ACTIVITIES_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: RECENT_CLUB_ACTIVITIES_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: MILESTONES_RECAP_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: MILESTONES_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: MISSIONS_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: [...USER_PROFILE_QUERY_KEY, currentUser?.id],
      });
      queryClient.invalidateQueries({
        queryKey: BUDDIES_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: MONTHLY_LEADERBOARD_QUERY_KEY,
      });
    },
  });
}

/**
 * Hook for reacting to an activity with an emoji
 * @returns Mutation for reacting to an activity
 */
export function useReactToActivity() {
  const queryClient = useQueryClient();

  return useMutation<
    ReactToActivityResponse,
    { message: string; status: number },
    ReactToActivityRequest
  >({
    mutationFn: async (data) => {
      const response = await client.activities.react.$post({
        json: data,
      });

      if (!response.ok) {
        const errorData = (await response.json()) as {
          message?: string;
          error?: string;
        };
        throw {
          message:
            errorData.message ||
            errorData.error ||
            "Failed to react to activity",
          status: response.status,
        };
      }

      return await response.json();
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch activities
      queryClient.invalidateQueries({
        queryKey: RECENT_CLUB_ACTIVITIES_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: USER_CLUBS_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: MILESTONES_RECAP_QUERY_KEY,
      });

      // Invalidate specific activity if we have it in the cache
      queryClient.invalidateQueries({
        queryKey: [...RECENT_CLUB_ACTIVITIES_QUERY_KEY],
        predicate: (query) => {
          const data = query.state.data as Activity[] | undefined;
          if (!data) return false;

          // Check if this query contains the activity we just reacted to
          return data.some((activity) => activity.id === variables.activityId);
        },
      });
    },
  });
}

/**
 * Hook for getting recent activities
 * @param userId Optional user ID to filter activities by user
 * @returns Query result with recent activities
 */
export function useRecentActivities(userId?: string) {
  return useQuery<Activity[], { message: string; status: number }>({
    queryKey: [...RECENT_CLUB_ACTIVITIES_QUERY_KEY, userId],
    queryFn: async () => {
      const response = await client.activities.$get({
        query: userId ? { userId } : {},
      });

      if (!response.ok) {
        throw {
          message: "Failed to fetch recent activities",
          status: response.status,
        };
      }

      return await response.json();
    },
  });
}
